<template>
    <div class="of-layout">
        <div class="of-main">
            <div class="of-tabs-wrap">
                <div class="of-tabs" role="tablist" aria-label="Fulfillment tabs">
                    <button class="of-tab" role="tab" :aria-selected="activeTab === 'shipments'"
                        :disabled="!hasShipments"
                        :title="!hasShipments ? 'Shipments will appear once we begin packing' : ''"
                        :class="{ active: activeTab === 'shipments', disabled: !hasShipments }"
                        @click="hasShipments && (activeTab = 'shipments')">
                        <span>Shipments</span>
                        <span class="of-pill" :class="hasShipments ? 'active' : ''">
                            {{ hasShipments ? shipmentsSorted.length : 'Pending' }}
                        </span>
                    </button>

                    <button class="of-tab" role="tab" :aria-selected="activeTab === 'items'"
                        :class="{ active: activeTab === 'items' }" @click="activeTab = 'items'">
                        <span>Order items</span>
                        <span class="of-pill active">{{ totalLineItems }}</span>
                    </button>

                    <button v-if="hasCancelItems" class="of-tab" role="tab" :aria-selected="activeTab === 'not_shipped'"
                        :class="{ active: activeTab === 'not_shipped', warn: activeTab === 'not_shipped' }"
                        @click="activeTab = 'not_shipped'">
                        <span class="of-tab-dot" aria-hidden="true"></span>
                        <span>Not shipped</span>
                        <span class="of-pill" :class="activeTab === 'not_shipped' ? 'warn' : ''">{{ notShippedProducts
                            }}</span>
                    </button>

                    <div class="of-tabs-right" v-if="activeTab === 'shipments' && hasShipments">
                        <span>{{ shipmentStatusSummary }}</span>
                        <button class="of-ghost-btn" @click="copyAllTracking">
                            <Copy class="w-3.5 h-3.5" />
                            <span>{{ copiedAllTracking ? 'Copied' : 'Copy tracking' }}</span>
                        </button>
                    </div>

                    <!-- <div class="of-tabs-right" v-else-if="activeTab === 'items'">
                        <a class="of-ghost-btn" :href="invoiceLink" target="_blank" rel="noopener" v-if="invoiceLink">
                            <Download class="w-3.5 h-3.5" />
                            <span>Export</span>
                        </a>
                    </div> -->

                    <div class="of-tabs-right" v-else></div>
                </div>
            </div>

            <div class="of-card--flush">

                <div class="of-panel of-panel-shipment" v-show="activeTab === 'shipments'">
                    <div v-if="hasShipments" class="of-shipments-list">
                        <Disclosure v-for="shipment in shipmentsSorted" :key="shipment.shipmentId" as="div"
                            class="of-shipment" :defaultOpen="shipmentsSorted.length === 1" v-slot="{ open }">
                            <DisclosureButton as="div" class="of-shipment-head">
                                <!-- LTL HEAD -->
                                <template v-if="shipment.mode === 'ltl'">
                                    <div class="of-head-grid of-head-grid--ltl">
                                        <div>
                                            <div class="of-carrier-row">
                                                <img :src="carrierLogo(shipment.carrier, shipment)" :alt="shipment.carrier"
                                                    class="of-carrier-logo" />
                                            </div>
                                            <div class="of-head-value of-head-value--strong">{{ carrierName(shipment.carrier, shipment) }}
                                            </div>
                                            <div class="of-head-freight-meta">{{ /ltl/i.test(shipment.carrier) ? 'Freight' : 'Local Delivery' }} · {{ shipment.boxes.length }}
                                                box{{ shipment.boxes.length === 1 ? '' : 'es' }}</div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">{{ /ltl/i.test(shipment.carrier) ? 'Pro Number' : 'Tracking Number' }}</div>
                                            <div class="of-inline-row">
                                                <a v-if="shipment.trackingUrl" :href="shipment.trackingUrl"
                                                    class="of-head-value" target="_blank" rel="noopener" @click.stop>
                                                    {{ shipment.trackingNumber || (/ltl/i.test(shipment.carrier) ? 'Track freight' : 'Track package') }}
                                                </a>
                                                <span v-else class="of-head-value">{{ shipment.trackingNumber ||
                                                    'Pending' }}</span>
                                                <button v-if="shipment.trackingNumber && /ltl/i.test(shipment.carrier)"
                                                    class="of-copy-btn" @click.stop="copyTracking(shipment)"
                                                    :title="copiedShipmentId === shipment.shipmentId ? 'Copied' : 'Copy tracking number'">
                                                    <Check v-if="copiedShipmentId === shipment.shipmentId"
                                                        class="w-3.5 h-3.5" />
                                                    <Copy v-else class="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            <div v-if="shipment.proNumber && shipment.proNumber !== shipment.trackingNumber"
                                                class="of-head-pro-number">
                                                PRO # {{ shipment.proNumber }}
                                            </div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">Shipped</div>
                                            <div class="of-head-value">{{ shipment.shipDateLabel || 'Pending' }}</div>
                                            <div v-if="shipment.deliveryDateLabel" class="of-head-expected"
                                                :class="shipment.deliveryDateTone">
                                                {{ shipment.deliveryDateLabel }}
                                            </div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">Status</div>
                                            <span class="of-status-pill" :class="shipment.statusClass">{{
                                                shipment.status }}</span>
                                        </div>
                                        <div class="of-head-units">
                                            <div class="of-head-units-stack">
                                                <span><b>{{ shipment.totalUnits }}</b> units</span>
                                                <span class="of-head-units-sub">in {{ shipment.boxes.length }} {{
                                                    shipment.boxes.length === 1 ? 'box' : 'boxes' }}</span>
                                            </div>
                                            <ChevronRightIcon class="w-4 h-4" :class="open ? 'rotate-90' : ''" />
                                        </div>
                                    </div>
                                </template>

                                <!-- PICKUP HEAD -->
                                <template v-else-if="shipment.mode === 'pickup'">
                                    <div class="of-head-grid of-head-grid--pickup">
                                        <div>
                                            <div class="of-carrier-row">
                                                <span class="of-pickup-icon">
                                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4"
                                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                        <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
                                                        <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
                                                        <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
                                                        <path
                                                            d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
                                                    </svg>
                                                </span>
                                            </div>
                                            <div class="of-head-value of-head-value--strong">Medusa Pickup</div>
                                        </div>
                                        <div v-if="shipment.pickedUpBy">
                                            <div class="of-head-label">Picked up by</div>
                                            <div class="of-head-value of-head-value--strong">{{ shipment.pickedUpBy }}
                                            </div>
                                            <div v-if="shipment.shipDateLabel" class="of-head-value of-head-value--sub">
                                                {{ shipment.shipDateLabel }}</div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">Ready on</div>
                                            <div class="of-head-value">{{ shipment.readyOn || shipment.shipDateLabel ||
                                                'Pending' }}</div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">Status</div>
                                            <span class="of-status-pill" :class="shipment.statusClass">{{
                                                shipment.status }}</span>
                                        </div>
                                        <div class="of-head-units">
                                            <div class="of-head-units-stack">
                                                <span><b>{{ shipment.totalUnits }}</b> units</span>
                                                <span class="of-head-units-sub">in {{ shipment.boxes.length }} {{
                                                    shipment.boxes.length === 1 ? 'box' : 'boxes' }}</span>
                                            </div>
                                            <ChevronRightIcon class="w-4 h-4" :class="open ? 'rotate-90' : ''" />
                                        </div>
                                    </div>
                                </template>

                                <!-- STANDARD HEAD -->
                                <template v-else>
                                    <div class="of-head-grid">
                                        <div>
                                            <div class="of-carrier-row">
                                                <img :src="carrierLogo(shipment.carrier)" :alt="shipment.carrier"
                                                    class="of-carrier-logo" />
                                            </div>
                                            <div class="of-head-value of-head-value--strong">{{ shipment.carrier }}
                                            </div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">Tracking</div>
                                            <div class="of-inline-row">
                                                <a v-if="shipment.trackingUrl" :href="shipment.trackingUrl"
                                                    class="of-head-value" target="_blank" rel="noopener" @click.stop>
                                                    {{ shipment.trackingNumber || 'Track package' }}
                                                </a>
                                                <span v-else class="of-head-value">{{ shipment.trackingNumber ||
                                                    'Pending' }}</span>
                                                <button v-if="shipment.trackingNumber" class="of-copy-btn"
                                                    @click.stop="copyTracking(shipment)"
                                                    :title="copiedShipmentId === shipment.shipmentId ? 'Copied' : 'Copy tracking number'">
                                                    <Check v-if="copiedShipmentId === shipment.shipmentId"
                                                        class="w-3.5 h-3.5" />
                                                    <Copy v-else class="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">Shipped</div>
                                            <div class="of-head-value">{{ shipment.shipDateLabel || 'Pending' }}</div>
                                            <div v-if="shipment.deliveryDateLabel" class="of-head-expected"
                                                :class="shipment.deliveryDateTone">
                                                {{ shipment.deliveryDateLabel }}
                                            </div>
                                        </div>
                                        <div>
                                            <div class="of-head-label">Status</div>
                                            <span class="of-status-pill" :class="shipment.statusClass">{{
                                                shipment.status }}</span>
                                        </div>
                                        <div class="of-head-units">
                                            <span><b>{{ shipment.totalUnits }}</b> units</span>
                                            <ChevronRightIcon class="w-4 h-4" :class="open ? 'rotate-90' : ''" />
                                        </div>
                                    </div>
                                </template>
                            </DisclosureButton>

                            <!-- LAST SCAN / DELIVERED / CONFIRMED BAR — always visible -->
                            <div v-if="shipment.timeline.length > 0 && !/^pending$/i.test(shipment.timeline[0].title)"
                                class="of-last-scan-bar" :class="{
                                    delivered: shipment.latestScanTone === 'delivered',
                                    confirmed: shipment.latestScanTone === 'confirmed',
                                    transit: shipment.latestScanTone === 'transit'
                                }">
                                <svg v-if="shipment.latestScanTone === 'confirmed'" xmlns="http://www.w3.org/2000/svg"
                                    class="of-last-scan-pin" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <svg v-else-if="shipment.latestScanTone === 'delivered'"
                                    xmlns="http://www.w3.org/2000/svg" class="of-last-scan-pin" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11z" />
                                    <circle cx="12" cy="10" r="2.5" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" class="of-last-scan-pin"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M5 12h11" />
                                    <path d="m12 8 4 4-4 4" />
                                    <circle cx="12" cy="12" r="9" />
                                </svg>
                                <span class="of-last-scan-label">{{ shipment.latestScanLabel }}</span>
                                <span class="of-last-scan-event">{{ shipment.latestScanDetail }}</span>
                                <span class="of-last-scan-time">{{ shipment.timeline[0].timeLabel }}</span>
                            </div>

                            <DisclosurePanel class="of-shipment-body">
                                <!-- LTL BODY -->
                                <template v-if="shipment.mode === 'ltl'">
                                    <div class="of-shipment-grid" style="padding:14px 18px">
                                        <div class="of-timeline">
                                            <div class="of-block-head of-block-head--inline">
                                                <span>Tracking timeline</span>
                                                <a v-if="shipment.trackingUrl" :href="shipment.trackingUrl"
                                                    target="_blank" rel="noopener"
                                                    class="of-ghost-btn of-track-link-btn">
                                                    <span>Track package</span>
                                                    <ExternalLink class="w-3 h-3" />
                                                </a>
                                                <button v-if="shipment.document"
                                                    class="of-ghost-btn of-track-link-btn"
                                                    @click.stop="openBolDocument(shipment.document)">
                                                    <FileText class="w-3 h-3" />
                                                    <span>Bill of Lading</span>
                                                </button>
                                            </div>
                                            <ol class="of-timeline-list">
                                                <li v-for="(event, idx) in visibleEvents(shipment)"
                                                    :key="`${shipment.shipmentId}-${idx}`" class="of-timeline-item">
                                                    <span class="of-timeline-dot"
                                                        :class="{ pending: !event.done }"></span>
                                                    <div>
                                                        <p>{{ event.title }}</p>
                                                        <span>{{ event.timeLabel }}<template v-if="event.location"> · {{
                                                            event.location }}</template></span>
                                                    </div>
                                                </li>
                                            </ol>
                                            <button v-if="hasHiddenEvents(shipment)" class="of-show-more-btn"
                                                @click.stop="toggleEventsShown(shipment.shipmentId)">
                                                {{ eventToggleLabel(shipment) }}
                                                <ChevronRightIcon class="w-3.5 h-3.5"
                                                    :class="allEventsShown(shipment.shipmentId) ? '-rotate-90' : 'rotate-90'" />
                                            </button>
                                        </div>
                                        <div>
                                            <div class="of-block-head">
                                                <span>Boxes ({{ shipment.boxes.length }})</span>
                                                <button v-if="shipment.boxes.length > 1"
                                                    class="of-link-btn"
                                                    @click.stop="toggleAllBoxes(shipment.shipmentId, shipment.boxes)">
                                                    {{ allBoxesExpanded(shipment.shipmentId, shipment.boxes) ? `Collapse
                                                    all` : `Expand all` }}
                                                </button>
                                            </div>
                                            <div class="of-box-list">
                                                <div v-for="(box, boxIndex) in visibleBoxes(shipment)" :key="box.id"
                                                    class="of-box"
                                                    :class="{ expanded: expandedBoxes[`${shipment.shipmentId}-${box.id}`] }">
                                                    <div class="of-box-head of-box-head--carton"
                                                        :class="{ expanded: expandedBoxes[`${shipment.shipmentId}-${box.id}`] }"
                                                        @click.stop="toggleBox(`${shipment.shipmentId}-${box.id}`)">
                                                        <div class="of-inline-row">
                                                            <ChevronRightIcon class="w-3.5 h-3.5"
                                                                :class="expandedBoxes[`${shipment.shipmentId}-${box.id}`] ? 'rotate-90' : ''" />
                                                            <span class="of-box-label">
                                                                <span>Box {{ boxIndex + 1 }}</span>
                                                                <span>of {{ shipment.boxes.length }}</span>
                                                            </span>
                                                        </div>
                                                        <div class="of-carton-chip">
                                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                                class="of-carton-icon" viewBox="0 0 40 36" fill="none">
                                                                <polygon points="20,2 38,11 20,20 2,11"
                                                                    fill="#e8a95a" />
                                                                <polygon points="2,11 20,20 20,34 2,25"
                                                                    fill="#c47c30" />
                                                                <polygon points="20,20 38,11 38,25 20,34"
                                                                    fill="#a85e1a" />
                                                                <line x1="20" y1="2" x2="20" y2="20" stroke="#c47c30"
                                                                    stroke-width="0.8" opacity="0.5" />
                                                                <line x1="2" y1="11" x2="38" y2="11" stroke="#c47c30"
                                                                    stroke-width="0.8" opacity="0.4" />
                                                            </svg>
                                                            <template v-if="box.carton">
                                                                <span class="of-carton-weight">{{
                                                                    formatCartonWeight(box.carton) }}</span>
                                                                <span class="of-carton-sep">·</span>
                                                                <span class="of-carton-dims">{{
                                                                    formatCartonDims(box.carton) }}</span>
                                                            </template>
                                                        </div>
                                                        <span class="of-box-meta">{{ box.units }} unit{{ box.units === 1
                                                            ? '' : 's' }}</span>
                                                    </div>
                                                    <OrderProductPreview v-show="expandedBoxes[`${shipment.shipmentId}-${box.id}`]" :items="box.items" v-slot="{ visibleItems }">
                                                        <div class="of-box-items">
                                                            <div v-for="(item, itemIdx) in visibleItems"
                                                                :key="`ltl-box-${box.id}-${itemIdx}`" class="of-item-row">
                                                                <img :src="item.image" :alt="item.name"
                                                                    class="of-item-image" />
                                                                <div class="of-item-main">
                                                                    <p>{{ item.name }}</p><span>{{ item.variant || 'Default'
                                                                        }}</span>
                                                                </div>
                                                                <div class="of-item-qty"><span>{{ item.qty }}</span><span
                                                                        class="of-item-qty-mark">×</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </OrderProductPreview>
                                                </div>
                                            </div>
                                            <button v-if="hasHiddenBoxes(shipment)" class="of-show-more-btn"
                                                @click.stop="toggleBoxesShown(shipment.shipmentId)">
                                                {{ boxToggleLabel(shipment) }}
                                                <ChevronRightIcon class="w-3.5 h-3.5"
                                                    :class="allBoxesShown(shipment.shipmentId) ? '-rotate-90' : 'rotate-90'" />
                                            </button>
                                        </div>
                                    </div>
                                </template>

                                <!-- PICKUP BODY -->
                                <template v-else-if="shipment.mode === 'pickup'">
                                    <div>
                                        <div class="of-block-head">
                                            <span>Boxes ({{ shipment.boxes.length }})</span>
                                            <button v-if="shipment.boxes.length > 1"
                                                class="of-link-btn"
                                                @click.stop="toggleAllBoxes(shipment.shipmentId, shipment.boxes)">
                                                {{ allBoxesExpanded(shipment.shipmentId, shipment.boxes) ? `Collapse
                                                all` : `Expand all` }}
                                            </button>
                                        </div>
                                        <div class="of-box-list">
                                            <div v-for="(box, boxIndex) in visibleBoxes(shipment)" :key="box.id"
                                                class="of-box"
                                                :class="{ expanded: expandedBoxes[`${shipment.shipmentId}-${box.id}`] }">
                                                <div class="of-box-head of-box-head--carton"
                                                    :class="{ expanded: expandedBoxes[`${shipment.shipmentId}-${box.id}`] }"
                                                    @click.stop="toggleBox(`${shipment.shipmentId}-${box.id}`)">
                                                    <div class="of-inline-row">
                                                        <ChevronRightIcon class="w-3.5 h-3.5"
                                                            :class="expandedBoxes[`${shipment.shipmentId}-${box.id}`] ? 'rotate-90' : ''" />
                                                        <span class="of-box-label">
                                                            <span>Box {{ boxIndex + 1 }}</span>
                                                            <span>of {{ shipment.boxes.length }}</span>
                                                        </span>
                                                    </div>
                                                    <div class="of-carton-chip">
                                                        <svg xmlns="http://www.w3.org/2000/svg" class="of-carton-icon"
                                                            viewBox="0 0 40 36" fill="none">
                                                            <polygon points="20,2 38,11 20,20 2,11" fill="#e8a95a" />
                                                            <polygon points="2,11 20,20 20,34 2,25" fill="#c47c30" />
                                                            <polygon points="20,20 38,11 38,25 20,34" fill="#a85e1a" />
                                                            <line x1="20" y1="2" x2="20" y2="20" stroke="#c47c30"
                                                                stroke-width="0.8" opacity="0.5" />
                                                            <line x1="2" y1="11" x2="38" y2="11" stroke="#c47c30"
                                                                stroke-width="0.8" opacity="0.4" />
                                                        </svg>
                                                        <template v-if="box.carton">
                                                            <span class="of-carton-weight">{{
                                                                formatCartonWeight(box.carton) }}</span>
                                                            <span class="of-carton-sep">·</span>
                                                            <span class="of-carton-dims">{{ formatCartonDims(box.carton)
                                                                }}</span>
                                                        </template>
                                                    </div>
                                                    <span class="of-box-meta">{{ box.units }} unit{{ box.units === 1 ?
                                                        '' : 's' }}</span>
                                                </div>
                                                <OrderProductPreview v-show="expandedBoxes[`${shipment.shipmentId}-${box.id}`]" :items="box.items" v-slot="{ visibleItems }">
                                                    <div class="of-box-items">
                                                        <div v-for="(item, itemIdx) in visibleItems"
                                                            :key="`pickup-box-${box.id}-${itemIdx}`" class="of-item-row">
                                                            <img :src="item.image" :alt="item.name" class="of-item-image" />
                                                            <div class="of-item-main">
                                                                <p>{{ item.name }}</p><span>{{ item.variant || 'Default'
                                                                    }}</span>
                                                            </div>
                                                            <div class="of-item-qty"><span>{{ item.qty }}</span><span
                                                                    class="of-item-qty-mark">×</span></div>
                                                        </div>
                                                    </div>
                                                </OrderProductPreview>
                                            </div>
                                        </div>
                                        <button v-if="hasHiddenBoxes(shipment)" class="of-show-more-btn"
                                            @click.stop="toggleBoxesShown(shipment.shipmentId)">
                                            {{ boxToggleLabel(shipment) }}
                                            <ChevronRightIcon class="w-3.5 h-3.5"
                                                :class="allBoxesShown(shipment.shipmentId) ? '-rotate-90' : 'rotate-90'" />
                                        </button>
                                    </div>
                                </template>

                                <!-- STANDARD BODY -->
                                <template v-else>
                                    <div class="of-shipment-grid">
                                        <div class="of-timeline">
                                            <div class="of-block-head of-block-head--inline">
                                                <span>Tracking timeline</span>
                                                <a v-if="shipment.trackingUrl" :href="shipment.trackingUrl"
                                                    target="_blank" rel="noopener"
                                                    class="of-ghost-btn of-track-link-btn">
                                                    <span>Track package</span>
                                                    <ExternalLink class="w-3 h-3" />
                                                </a>
                                            </div>
                                            <ol class="of-timeline-list">
                                                <li v-for="(event, idx) in visibleEvents(shipment)"
                                                    :key="`${shipment.shipmentId}-${idx}`" class="of-timeline-item">
                                                    <span class="of-timeline-dot"
                                                        :class="{ pending: !event.done }"></span>
                                                    <div>
                                                        <p>{{ event.title }}</p>
                                                        <span>{{ event.timeLabel }}<template v-if="event.location"> · {{
                                                            event.location }}</template></span>
                                                    </div>
                                                </li>
                                            </ol>
                                            <button v-if="hasHiddenEvents(shipment)" class="of-show-more-btn"
                                                @click.stop="toggleEventsShown(shipment.shipmentId)">
                                                {{ eventToggleLabel(shipment) }}
                                                <ChevronRightIcon class="w-3.5 h-3.5"
                                                    :class="allEventsShown(shipment.shipmentId) ? '-rotate-90' : 'rotate-90'" />
                                            </button>
                                        </div>
                                        <div>
                                            <template v-if="shipment.carton">
                                                <div class="of-block-head"><span>Carton</span></div>
                                                <div class="of-carton-block">
                                                    <svg xmlns="http://www.w3.org/2000/svg" class="of-carton-icon"
                                                        viewBox="0 0 40 36" fill="none">
                                                        <polygon points="20,2 38,11 20,20 2,11" fill="#e8a95a" />
                                                        <polygon points="2,11 20,20 20,34 2,25" fill="#c47c30" />
                                                        <polygon points="20,20 38,11 38,25 20,34" fill="#a85e1a" />
                                                        <line x1="20" y1="2" x2="20" y2="20" stroke="#c47c30"
                                                            stroke-width="0.8" opacity="0.5" />
                                                        <line x1="2" y1="11" x2="38" y2="11" stroke="#c47c30"
                                                            stroke-width="0.8" opacity="0.4" />
                                                    </svg>
                                                    <span class="of-carton-text">{{ formatCartonWeight(shipment.carton)
                                                        }} · {{ formatCartonDims(shipment.carton) }}</span>
                                                </div>
                                            </template>

                                            <template v-if="shipment.boxes.length">
                                                <div class="of-block-head"><span>Boxes ({{ shipment.boxes.length
                                                        }})</span></div>
                                                <div class="of-box-list">
                                                    <Disclosure v-for="(box, boxIndex) in visibleBoxes(shipment)"
                                                        :key="box.id" as="div" class="of-box"
                                                        v-slot="{ open: boxOpen }">
                                                        <DisclosureButton as="div" class="of-box-head"
                                                            :class="{ expanded: boxOpen }">
                                                            <div class="of-inline-row">
                                                                <ChevronRightIcon class="w-3.5 h-3.5"
                                                                    :class="boxOpen ? 'rotate-90' : ''" />
                                                                <span class="of-box-label">
                                                                    <span>Box {{ boxIndex + 1 }}</span>
                                                                    <span>of {{ shipment.boxes.length }}</span>
                                                                </span>
                                                            </div>
                                                            <span class="of-box-meta">{{ box.units }} units</span>
                                                        </DisclosureButton>
                                                        <DisclosurePanel>
                                                            <OrderProductPreview :items="box.items" v-slot="{ visibleItems }">
                                                                <div class="of-box-items">
                                                                    <div v-for="(item, itemIdx) in visibleItems"
                                                                        :key="`box-${box.id}-${itemIdx}`"
                                                                        class="of-item-row">
                                                                        <img :src="item.image" :alt="item.name"
                                                                            class="of-item-image" />
                                                                        <div class="of-item-main">
                                                                            <p>{{ item.name }}</p><span>{{ item.variant ||
                                                                                'Default' }}</span>
                                                                        </div>
                                                                        <div class="of-item-qty"><span>{{ item.qty
                                                                        }}</span><span
                                                                                class="of-item-qty-mark">×</span></div>
                                                                    </div>
                                                                </div>
                                                            </OrderProductPreview>
                                                        </DisclosurePanel>
                                                    </Disclosure>
                                                </div>
                                                <button v-if="hasHiddenBoxes(shipment)" class="of-show-more-btn"
                                                    @click.stop="toggleBoxesShown(shipment.shipmentId)">
                                                    {{ boxToggleLabel(shipment) }}
                                                    <ChevronRightIcon class="w-3.5 h-3.5"
                                                        :class="allBoxesShown(shipment.shipmentId) ? '-rotate-90' : 'rotate-90'" />
                                                </button>
                                            </template>

                                            <template v-else>
                                                <div class="of-block-head"><span>Items in shipment ({{
                                                    shipment.items.length }})</span></div>
                                                <div class="of-box of-items-in-shipment">
                                                    <OrderProductPreview :items="shipment.items" v-slot="{ visibleItems }">
                                                        <div class="of-box-items">
                                                            <div v-for="(item, itemIdx) in visibleItems"
                                                                :key="`shipment-${shipment.shipmentId}-${itemIdx}`"
                                                                class="of-item-row">
                                                                <img :src="item.image" :alt="item.name"
                                                                    class="of-item-image" />
                                                                <div class="of-item-main">
                                                                    <p>{{ item.name }}</p><span>{{ item.variant || 'Default'
                                                                    }}</span>
                                                                </div>
                                                                <div class="of-item-qty"><span>{{ item.qty }}</span><span
                                                                        class="of-item-qty-mark">×</span></div>
                                                            </div>
                                                        </div>
                                                    </OrderProductPreview>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                            </DisclosurePanel>
                        </Disclosure>
                    </div>

                    <div v-else class="of-empty-state">
                        <h4>Shipments pending</h4>
                        <p>We have received your order and are preparing packages.</p>
                    </div>
                </div>

                <div class="of-panel" v-show="activeTab === 'items'">
                    <template v-if="hasShipments">
                        <div class="of-package-items">
                            <div class="of-package-items-head">
                                <div>
                                    <span class="of-package-title">Grouped by package</span>
                                    <span class="of-package-total">· {{ totalUnits }} units total</span>
                                </div>
                                <label class="of-items-search" aria-label="Search products in this order">
                                    <Search class="w-3.5 h-3.5" />
                                    <input v-model="itemSearchTerm" type="text"
                                        placeholder="Search products in this order..." />
                                </label>
                            </div>

                            <div v-for="(shipment, shipmentIdx) in filteredItemGroupsByShipment"
                                :key="`group-${shipment.shipmentId}`" class="of-package-group">
                                <div class="of-package-group-head">
                                    <span class="of-package-number">#{{ shipmentIdx + 1 }}</span>
                                    <img :src="carrierLogo(shipment.carrier)" :alt="shipment.carrier"
                                        class="of-package-logo" />
                                    <span class="of-package-carrier">{{ carrierName(shipment.carrier) }}</span>
                                    <span v-if="shipment.trackingNumber" class="of-package-tracking">
                                        {{ shipment.trackingNumber }}
                                    </span>
                                    <button v-if="shipment.trackingNumber" class="of-copy-btn"
                                        @click.stop="copyTracking(shipment)"
                                        :title="copiedShipmentId === shipment.shipmentId ? 'Copied' : 'Copy tracking number'">
                                        <Check v-if="copiedShipmentId === shipment.shipmentId" class="w-3.5 h-3.5" />
                                        <Copy v-else class="w-3.5 h-3.5" />
                                    </button>
                                    <span class="of-status-pill" :class="shipment.statusClass">{{ shipment.status
                                        }}</span>
                                </div>

                                <OrderProductPreview :items="shipment.items" :limit-height="false" row-selector=".of-package-product" v-slot="{ visibleItems }">
                                    <div class="of-package-table">
                                        <div class="of-package-colhead">Product</div>
                                        <div class="of-package-colhead right">Qty</div>
                                        <div class="of-package-colhead right">Total</div>

                                        <template v-for="(item, itemIdx) in visibleItems"
                                            :key="`package-${shipment.shipmentId}-${itemIdx}`">
                                            <div class="of-package-product">
                                                <img :src="item.image" :alt="item.name" class="of-package-item-image" />
                                                <div class="of-package-item-main">
                                                    <p class="of-item-line1">
                                                        <span v-if="item.brand" class="of-item-brand">{{ item.brand }}</span>
                                                        <span v-if="item.brand" class="of-item-sep">-</span>
                                                        <span class="of-item-name">{{ item.name }}</span>
                                                    </p>
                                                    <p class="of-item-line2">
                                                        <span class="of-item-variant">{{ item.variant || 'Default' }}</span>
                                                        <template v-if="item.sku">
                                                            <span class="of-item-sep">-</span>
                                                            <span class="of-item-meta-val">{{ item.sku }}</span>
                                                            <button class="of-sku-copy-btn" @click.stop="copySku(item.sku)"
                                                                :title="copiedSku === item.sku ? 'Copied' : 'Copy SKU'">
                                                                <Check v-if="copiedSku === item.sku" class="w-3 h-3" />
                                                                <Copy v-else class="w-3 h-3" />
                                                            </button>
                                                        </template>
                                                        <!-- <template v-if="item.upc">
                                                            <span class="of-item-sep">-</span>
                                                            <span class="of-item-meta-val">{{ item.upc }}</span>
                                                        </template> -->
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="of-package-qty">{{ item.qty }}</div>
                                            <div class="of-package-total-cell">{{ money(item.lineTotal) }}</div>
                                        </template>
                                    </div>
                                </OrderProductPreview>
                            </div>

                            <div v-if="!filteredItemGroupsByShipment.length" class="of-items-empty">
                                No items match your search.
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <div class="of-items-header">
                            <div class="of-items-header-main">
                                <span class="of-items-header-title">Items in your order</span>
                                <span class="of-items-header-meta">· {{ totalUnits }} units</span>
                            </div>

                            <label class="of-items-search" aria-label="Search products in this order">
                                <Search class="w-3.5 h-3.5" />
                                <input v-model="itemSearchTerm" type="text"
                                    placeholder="Search products in this order..." />
                            </label>
                        </div>

                        <div class="of-items-allocation">
                            <div class="of-items-allocation-left">
                                <span class="of-items-allocation-title">Not yet assigned to a box</span>
                                <span class="of-items-alloc-pill">
                                    <span class="of-items-alloc-dot"></span>
                                    <span>Allocating</span>
                                </span>
                            </div>
                            <span class="of-items-allocation-meta">{{ totalUnits }} units · being packed</span>
                        </div>

                        <div class="of-items-table">
                            <div class="of-items-colhead">Product</div>
                            <div class="of-items-colhead right">Qty</div>
                            <div class="of-items-colhead right">Total</div>

                            <template v-for="(item, itemIdx) in filteredPendingItems" :key="`pending-item-${itemIdx}`">
                                <div class="of-items-product-cell">
                                    <img :src="item.image" :alt="item.name" class="of-item-image" />
                                    <div class="of-item-main">
                                        <p>{{ item.name }}</p>
                                        <span>{{ item.variant || 'Default' }}</span>
                                    </div>
                                </div>
                                <div class="of-items-qty-cell">{{ item.qty }}</div>
                                <div class="of-items-total-cell">{{ money(item.lineTotal) }}</div>
                            </template>

                            <div v-if="!filteredPendingItems.length" class="of-items-empty">
                                No items match your search.
                            </div>
                        </div>
                    </template>
                </div>

                <div class="of-panel" v-show="activeTab === 'not_shipped'">
                    <div class="of-not-shipped-card">
                        <div class="of-not-shipped-banner">
                            <span class="of-not-shipped-icon" aria-hidden="true">
                                <AlertTriangle class="w-3.5 h-3.5" />
                            </span>
                            <div class="of-not-shipped-copy">
                                <div>
                                    <b>{{ notShippedUnits }} units</b> across <b>{{ notShippedProducts }} {{ notShippedProducts === 1 ? 'product' : 'products' }}</b>
                                    couldn't be shipped.
                                    The invoice has been reduced by <b>{{ money(notShippedTotal) }}</b>.
                                </div>
                                <div class="of-not-shipped-note">
                                    We're sorry. On rare occasion, our inventory count is slightly off, or we spot
                                    damage during packing and pull the item rather than ship something that isn't right.
                                    You haven't been charged for anything that couldn't ship.
                                </div>
                            </div>
                            <!-- <a class="of-ghost-btn of-not-shipped-invoice" :href="invoiceLink" target="_blank"
                                rel="noopener" v-if="invoiceLink">
                                <FileText class="w-3.5 h-3.5" />
                                <span>Updated invoice</span>
                            </a> -->
                        </div>

                        <div class="of-items-table of-not-items-table">
                            <div class="of-items-colhead">Product</div>
                            <div class="of-items-colhead right">Qty</div>
                            <div class="of-items-colhead right">Removed</div>

                            <template v-for="(item, idx) in notShippedItems" :key="`not-shipped-${idx}`">
                                <div class="of-items-product-cell">
                                    <img :src="item.image" :alt="item.name" class="of-item-image" />
                                    <div class="of-item-main">
                                        <p>{{ item.name }}</p>
                                        <span>{{ item.variant }}</span>
                                    </div>
                                </div>
                                <div class="of-items-qty-cell">{{ item.qty }}</div>
                                <div class="of-items-total-cell of-not-removed-cell">-{{ money(item.removed) }}</div>
                            </template>
                        </div>

                        <div class="of-not-adjust-row">
                            <div class="of-not-adjust-label">Invoice adjustment</div>
                            <div class="of-not-adjust-units">{{ notShippedUnits }} units</div>
                            <div class="of-not-adjust-total">-{{ money(notShippedTotal) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <aside class="of-rail">
            <div class="of-card of-pad">
                <div class="of-rail-label">Ship to</div>
                <div class="of-rail-title">{{ shippingName }}</div>
                <div class="of-rail-text">{{ shippingAddress }}</div>
            </div>

            <div class="of-card of-pad">
                <OrderInformationCard :rows="orderInfoRows" :copied-key="copiedFieldKey"
                    :on-copy="handleOrderInfoCopy" />

                <div class="of-rep">
                    <div class="of-rail-label">Your rep</div>
                    <div class="of-rep-row">
                        <span class="of-rep-avatar">{{ repInitials }}</span>
                        <div>
                            <p class="of-rep-name">{{ repName }}</p>
                            <a v-if="repPhone" :href="`tel:${repPhone}`" class="of-rep-link">{{ repPhone }}</a>
                            <a v-if="repEmail" :href="`mailto:${repEmail}`" class="of-rep-link">{{ repEmail }}</a>
                            <a v-if="repEmail" href="#" @click="onRepContact"
                                class="of-rep-link of-rep-link--action">Contact
                                rep</a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="of-card of-summary">
                <div class="of-summary-head">Order Summary</div>

                <div class="of-summary-rows">
                    <div class="of-info-row">
                        <span>Subtotal</span>
                        <span>{{ money(summary.subtotal) }}</span>
                    </div>
                    <div class="of-info-row">
                        <span>Shipping</span>
                        <span>{{ money(summary.shipping) }}</span>
                    </div>
                    <div class="of-info-row" v-if="summary.tax > 0">
                        <span>Tax</span>
                        <span>{{ money(summary.tax) }}</span>
                    </div>
                    <div v-if="showPaidAmountRow" class="of-info-row of-info-row--paid">
                        <span>Paid</span>
                        <span>+ {{ money(paidAmount) }}</span>
                    </div>
                </div>

                <div class="of-summary-total">
                    <span>{{ summaryTotalLabel }}</span>
                    <span>{{ money(summaryTotalAmount) }}</span>
                </div>

                <div class="of-summary-payments">
                    <div v-for="(payment, idx) in paymentRows" :key="`payment-${idx}`" class="of-payment-row">
                        <div class="of-payment-row-stack">
                            <div class="of-payment-row-main">
                                <span class="paid-mark">
                                    <Check class="w-3 h-3" />
                                </span>
                                <span class="of-payment-paid-label">Paid</span>
                                <span v-if="payment.isVisa" class="of-payment-chip">VISA</span>
                                <span v-else class="of-payment-method">{{ payment.label }}</span>
                                <span v-if="payment.last4" class="of-payment-last4">•• {{ payment.last4 }}</span>
                                <span v-else-if="payment.reference" class="of-payment-reference">· {{
                                    payment.reference }}</span>
                            </div>
                            <span v-if="payment.date" class="of-payment-date">{{ payment.date }}</span>
                        </div>
                        <span class="of-payment-amount">{{ money(payment.amount) }}</span>
                    </div>

                    <button v-if="balance > 0 && order.company?.IsUSA != 0" class="of-pay-btn"
                        @click="goToPayInvoice">
                        <CreditCard class="w-3.5 h-3.5" />
                        <span>Pay Invoice ({{ money(balance) }})</span>
                    </button>
                </div>

                <div class="of-summary-footer">
                    <div class="of-info-row" v-if="customerTerms">
                        <span>Payment terms</span>
                        <span>{{ customerTerms }}</span>
                    </div>
                    <div class="of-info-row" v-if="balance > 0 && dueDate">
                        <span>Due date</span>
                        <span class="of-due-date-value" :class="{ overdue: overdueDays > 0 }">{{ dueDate }}</span>
                    </div>
                    <div v-if="balance > 0 && overdueDays > 0" class="of-overdue-note">
                        {{ overdueDays }} day{{ overdueDays === 1 ? '' : 's' }} overdue
                    </div>
                    <a class="of-ghost-btn of-full" :href="invoiceLink" target="_blank" rel="noopener"
                        v-if="invoiceLink">
                        <FileText class="w-3.5 h-3.5" />
                        <span>Download Invoice</span>
                    </a>
                </div>
            </div>
        </aside>
    </div>
</template>

<script setup>
import { computed, reactive, ref, watch, inject } from "vue";
import { useRouter } from "vue-router";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ChevronRightIcon } from "@heroicons/vue/solid";
import { Check, Copy, Download, CreditCard, FileText, Search, AlertTriangle, ExternalLink } from "lucide-vue-next";
import OrderInformationCard from "./OrderInformationCard.vue";
import OrderProductPreview from "./OrderProductPreview.vue";

const props = defineProps({
    order: {
        type: Object,
        default: () => ({}),
    },
    shipments: {
        type: Array,
        default: () => [],
    },
});

const axios = inject("axios");
const router = useRouter();

const goToPayInvoice = () => {
    const companyId = props.order?.encrypted_company_id;
    if (!companyId) return;
    router.push(`/invoice-payments/${companyId}`);
};

const activeTab = ref("shipments");
const copiedShipmentId = ref(null);
const copiedAllTracking = ref(false);
const copiedFieldKey = ref("");
const copiedSku = ref("");
const itemSearchTerm = ref("");
const expandedBoxes = reactive({});
const expandedEventLists = reactive({});
const expandedBoxLists = reactive({});
const PREVIEW_LIMIT = 4;

const toggleBox = (key) => {
    expandedBoxes[key] = !expandedBoxes[key];
};

const toggleAllBoxes = (shipmentId, boxes) => {
    const anyExpanded = boxes.some(b => expandedBoxes[`${shipmentId}-${b.id}`]);
    boxes.forEach(b => {
        expandedBoxes[`${shipmentId}-${b.id}`] = !anyExpanded;
    });
};

const allBoxesExpanded = (shipmentId, boxes) => {
    return boxes.length > 0 && boxes.every(b => expandedBoxes[`${shipmentId}-${b.id}`]);
};

const allEventsShown = (shipmentId) => expandedEventLists[shipmentId] === true;

const allBoxesShown = (shipmentId) => expandedBoxLists[shipmentId] === true;

const hasHiddenEvents = (shipment) => {
    return (shipment.timeline || []).length > PREVIEW_LIMIT;
};

const hasHiddenBoxes = (shipment) => {
    return (shipment.boxes || []).length > PREVIEW_LIMIT;
};

const visibleEvents = (shipment) => {
    const events = (shipment.timeline || []).slice();
    return allEventsShown(shipment.shipmentId) ? events : events.slice(0, PREVIEW_LIMIT);
};

const visibleBoxes = (shipment) => {
    const boxes = shipment.boxes || [];
    return allBoxesShown(shipment.shipmentId) ? boxes : boxes.slice(0, PREVIEW_LIMIT);
};

const toggleEventsShown = (shipmentId) => {
    expandedEventLists[shipmentId] = !expandedEventLists[shipmentId];
};

const toggleBoxesShown = (shipmentId) => {
    expandedBoxLists[shipmentId] = !expandedBoxLists[shipmentId];
};

const eventToggleLabel = (shipment) => {
    return allEventsShown(shipment.shipmentId)
        ? "Show fewer events"
        : `Show all ${shipment.timeline.length} events`;
};

const boxToggleLabel = (shipment) => {
    return allBoxesShown(shipment.shipmentId)
        ? "Show fewer boxes"
        : `Show all ${shipment.boxes.length} boxes`;
};

const formatCartonWeight = (carton) => {
    if (!carton) return '';
    const uom = String(carton.weightUOM || '').toLowerCase();
    const unit = uom.startsWith('pound') ? 'lbs' : uom.startsWith('kilo') ? 'kg' : (carton.weightUOM || 'lbs');
    return `${parseFloat(carton.weight || 0)} ${unit}`;
};

const formatCartonDims = (carton) => {
    if (!carton) return '';
    const l = parseFloat(carton.length || 0);
    const w = parseFloat(carton.width || 0);
    const h = parseFloat(carton.height || 0);
    return `${l}"×${w}"×${h}"`;
};

const parseBolDocument = (doc) => {
    if (!doc) return null;
    try {
        const parsed = JSON.parse(doc);
        const path = Array.isArray(parsed) ? parsed[0] : parsed;
        return path || null;
    } catch {
        return typeof doc === 'string' ? doc : null;
    }
};

const openBolDocument = async (doc) => {
    const path = parseBolDocument(doc);
    if (!path) return;

    const { data } = await axios.authApi.get('/account/get-file', {
        params: { path },
        responseType: 'blob',
    });

    const blobUrl = URL.createObjectURL(data);
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
};
const order = computed(() => props.order || {});

const RAIL_FALLBACKS = {
    shippingName: "The Glass Station (AR)",
    shippingAddress: "434 N College Ave\n#C\nFayetteville, Arkansas 72701",
    orderNumber: "MD566254",
    invoiceNumber: "INC395713",
    referenceNumber: "GS-2025-Q3-014",
    placedBy: "Marcus Whitfield",
    placedAt: "Jun 11, 2025",
    paymentStatus: "Unpaid",
    repName: "Maya Lin",
    repEmail: "maya.lin@medusadistribution.com",
    repPhone: "+1 (332) 603-1711",
};


const toNumber = (value) => {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    }).format(toNumber(value));
};

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date);
};

const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    }).format(date);
};

const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeItem = (item) => {
    const qty = toNumber(item.ship_qty || item.qty || item.quantity || 0);
    const unitPrice = toNumber(
        item.singlePrice ||
        item.unit_price ||
        item.unitPrice ||
        item.price ||
        item.variant_price ||
        item.selling_price ||
        0
    );
    const lineTotal = toNumber(item.total_price || item.line_total || item.lineTotal || item.total || unitPrice * qty);

    return {
        name: item.product_name || item.productName || item.name || "Item",
        variant: item.variant_name || item.variant || item.optionLabel || "",
        brand: item.brand_name || item.brand || "",
        sku: item.item_number || item.sku || item.variant_sku || item.product_sku || "",
        upc: item.upc || item.barcode || "",
        qty,
        image: item.variant_image || item.product_image || item.image_url || "/images/no-image.png",
        unitPrice,
        lineTotal,
    };
};

const sortBoxItems = (items) =>
    [...items].sort((a, b) => {
        const n = (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
        if (n !== 0) return n;
        return (a.variant || "").toLowerCase().localeCompare((b.variant || "").toLowerCase());
    });

const normalizeBox = (box, boxIndex = 0) => {
    const items = Array.isArray(box.items) ? sortBoxItems(box.items.map(normalizeItem)) : [];
    return {
        id: box.id || box.box_id || `box-${boxIndex}`,
        units: items.reduce((sum, item) => sum + toNumber(item.qty), 0),
        items,
    };
};

const normalizeTimeline = (shipment) => {
    const source = Array.isArray(shipment.timeline)
        ? shipment.timeline
        : Array.isArray(shipment.events)
            ? shipment.events
            : [];

    if (!source.length) {
        return [
            {
                title: shipment.status || "Pending",
                timeLabel: shipment.shipDateLabel || "Pending",
                dateLabel: formatDate(shipment.deliveredDate || shipment.shipDate) || "",
                location: "",
                done: !/pending|awaiting/i.test(String(shipment.status || "")),
            },
        ];
    }

    return source.map((event) => {
        const eventTime = event.time || event.at || event.date || event.datetime || event.timestamp;
        return {
            title: event.title || event.label || event.status || "Update",
            timeLabel: event.timeLabel || formatDateTime(eventTime) || "Pending",
            dateLabel: event.dateLabel || formatDate(eventTime) || "",
            location: event.location || "",
            done: event.done !== false,
        };
    });
};

const getDeliveredEvent = (timeline = []) => {
    return timeline.find((event) => /deliver/i.test(String(event.title || "")));
};

const getDeliveryDateMeta = (shipment = {}) => {
    const deliveredEvent = getDeliveredEvent(shipment.timeline);
    const deliveredDate = formatDate(shipment.deliveredDate) || deliveredEvent?.dateLabel || "";
    const statusText = `${shipment.statusClass || ""} ${shipment.status || ""}`;

    if (/deliver/i.test(statusText) || deliveredEvent) {
        return {
            deliveryDateLabel: deliveredDate ? `Delivered ${deliveredDate}` : "Delivered",
            deliveryDateTone: "delivered",
        };
    }

    if (shipment.estimatedDelivery) {
        return {
            deliveryDateLabel: `Expected ${shipment.estimatedDelivery}`,
            deliveryDateTone: "expected",
        };
    }

    return {
        deliveryDateLabel: "",
        deliveryDateTone: "",
    };
};

const getLatestScanMeta = (shipment = {}) => {
    const latestEvent = shipment.timeline?.[0] || {};
    const latestTitle = String(latestEvent.title || "");
    const latestLocation = String(latestEvent.location || "");
    const latestDetail = [latestTitle, latestLocation].filter(Boolean).join(" · ");
    const deliveredDetail = [
        latestTitle.replace(/^delivered,?\s*/i, "").trim(),
        latestLocation,
    ].filter(Boolean).join(" · ");
    const statusText = `${shipment.statusClass || ""} ${shipment.status || ""} ${latestTitle}`;

    if (/confirmed|picked up/i.test(statusText)) {
        return {
            latestScanTone: "confirmed",
            latestScanLabel: "Latest pickup activity",
            latestScanDetail: latestDetail,
        };
    }

    if (/deliver/i.test(statusText)) {
        return {
            latestScanTone: "delivered",
            latestScanLabel: "Last Activity",
            latestScanDetail: deliveredDetail || latestLocation || latestTitle,
        };
    }

    if (/created/i.test(latestTitle)) {
        return {
            latestScanTone: "created",
            latestScanLabel: "Latest tracking activity",
            latestScanDetail: latestDetail,
        };
    }

    if (/transit|shipped|way|vehicle|facility|arriv|depart|dispatch/i.test(statusText)) {
        return {
            latestScanTone: "transit",
            latestScanLabel: "Latest tracking activity",
            latestScanDetail: latestDetail,
        };
    }

    return {
        latestScanTone: "neutral",
        latestScanLabel: "Latest tracking activity",
        latestScanDetail: latestDetail,
    };
};

const attachShipmentUiMeta = (shipment = {}) => ({
    ...shipment,
    ...getDeliveryDateMeta(shipment),
    ...getLatestScanMeta(shipment),
});

const shipmentsSorted = computed(() => {
    const rawList = Array.isArray(props.shipments) ? props.shipments : [];
    if (!rawList.length) return [];

    const firstCarrier = rawList[0]?.carrier || "";
    const isPickupOrder = /pick.?up.?at.?medusa|medusa.?pick.?up/i.test(firstCarrier);
    const isLTLOrder = !isPickupOrder && /\bltl\b|\bmdx\b/i.test(firstCarrier);

    const makeBoxList = (shipments) =>
        shipments.map((s, i) => ({
            id: s.shipmentId || `box-${i}`,
            carton: s.carton || null,
            items: Array.isArray(s.items) ? sortBoxItems(s.items.map(normalizeItem)) : [],
            units: toNumber(s.totalUnits) || (Array.isArray(s.items)
                ? s.items.reduce((acc, it) => acc + toNumber(it.ship_qty || it.qty || 0), 0)
                : 0),
        }));

    if (isPickupOrder) {
        const boxes = makeBoxList(rawList);
        const first = rawList[0];
        const totalUnits = boxes.reduce((sum, b) => sum + b.units, 0);
        return [attachShipmentUiMeta({
            shipmentId: 'pickup-merged',
            carrier: first.carrier || 'Medusa Pickup',
            trackingNumber: '',
            trackingUrl: '',
            shipDateLabel: formatDateTime(first.shipDate),
            estimatedDelivery: null,
            deliveredDate: first.deliveredDate || null,
            status: first.status || 'Delivered',
            statusClass: first.statusClass || 'delivered',
            document: first.document || null,
            pickedUpBy: first.pickedUpBy || null,
            readyOn: formatDate(first.readyOn || first.shipDate),
            timeline: normalizeTimeline({ ...first, shipDateLabel: formatDate(first.shipDate) }),
            items: boxes.flatMap(b => b.items),
            boxes,
            carton: null,
            totalUnits,
            mode: 'pickup',
        })];
    }

    if (isLTLOrder) {
        const groups = new Map();
        rawList.forEach(s => {
            const key = s.trackingNumber || s.shipmentId;
            if (!groups.has(key)) {
                groups.set(key, {
                    shipmentId: key,
                    carrier: s.carrier,
                    carrierName: s.carrierName || "",
                    carrierImageUrl: s.carrierImageUrl || "",
                    carrierBaseUrl: s.carrierBaseUrl || "",
                    trackingNumber: s.trackingNumber,
                    proNumber: s.proNumber || null,
                    trackingUrl: s.trackingUrl,
                    shipDateLabel: formatDateTime(s.shipDate),
                    estimatedDelivery: s.estimatedDeliveryDate ? formatDate(s.estimatedDeliveryDate) : null,
                    deliveredDate: s.deliveredDate || null,
                    status: s.status,
                    statusClass: s.statusClass,
                    document: s.document,
                    timeline: normalizeTimeline({ ...s, shipDateLabel: formatDate(s.shipDate) }),
                    boxes: [],
                    items: [],
                    carton: null,
                    totalUnits: 0,
                    mode: 'ltl',
                });
            }
            const group = groups.get(key);
            if (!group.carrierName && s.carrierName) group.carrierName = s.carrierName;
            if (!group.carrierImageUrl && s.carrierImageUrl) group.carrierImageUrl = s.carrierImageUrl;
            if (!group.carrierBaseUrl && s.carrierBaseUrl) group.carrierBaseUrl = s.carrierBaseUrl;
            const boxItems = Array.isArray(s.items) ? sortBoxItems(s.items.map(normalizeItem)) : [];
            const boxUnits = boxItems.reduce((sum, it) => sum + toNumber(it.qty), 0);
            group.boxes.push({ id: s.shipmentId, carton: s.carton || null, items: boxItems, units: boxUnits });
            group.items.push(...boxItems);
            group.totalUnits += boxUnits;
            const newTl = normalizeTimeline({ ...s, shipDateLabel: formatDate(s.shipDate) });
            if (newTl.length > group.timeline.length) {
                group.timeline = newTl;
            }
        });
        return Array.from(groups.values()).map(attachShipmentUiMeta);
    }

    return rawList
        .map((shipment, idx) => {
            const boxesSource =
                (Array.isArray(shipment.boxes) && shipment.boxes) ||
                (Array.isArray(shipment.packages) && shipment.packages) ||
                (Array.isArray(shipment.cartons) && shipment.cartons) ||
                [];

            const normalizedBoxes = boxesSource.map((box, boxIndex) => normalizeBox(box, boxIndex));
            const normalizedItems = sortBoxItems(Array.isArray(shipment.items)
                ? shipment.items.map(normalizeItem)
                : normalizedBoxes.flatMap((box) => box.items));

            const status = shipment.status || "Pending";
            const statusClass = String(shipment.statusClass || "").toLowerCase();

            return {
                shipmentId: shipment.shipmentId || shipment.id || `shipment-${idx + 1}`,
                carrier: shipment.carrier || "Carrier",
                trackingNumber: shipment.trackingNumber || "",
                trackingUrl: shipment.trackingUrl || "",
                shipDateLabel: formatDateTime(shipment.shipDate),
                estimatedDelivery: shipment.estimatedDeliveryDate ? formatDate(shipment.estimatedDeliveryDate) : null,
                deliveredDate: shipment.deliveredDate || null,
                status,
                statusClass,
                document: shipment.document || null,
                carton: shipment.carton || null,
                totalUnits:
                    toNumber(shipment.totalUnits) ||
                    normalizedItems.reduce((sum, item) => sum + toNumber(item.qty), 0),
                timeline: normalizeTimeline({ ...shipment, status, shipDateLabel: formatDate(shipment.shipDate) }),
                items: normalizedItems,
                boxes: normalizedBoxes,
                mode: 'standard',
            };
        })
        .map(attachShipmentUiMeta)
        .sort((a, b) => {
            if (a.statusClass.includes("delivered") && !b.statusClass.includes("delivered")) return 1;
            if (!a.statusClass.includes("delivered") && b.statusClass.includes("delivered")) return -1;
            return 0;
        });
});

const hasShipments = computed(() => shipmentsSorted.value.length > 0);

watch(
    hasShipments,
    (value) => {
        if (!value) {
            activeTab.value = "items";
        }
    },
    { immediate: true }
);

watch(hasCancelItems, (value) => {
    if (!value && activeTab.value === "not_shipped") {
        activeTab.value = hasShipments.value ? "shipments" : "items";
    }
});

const totalLineItems = computed(() => {
    return Array.isArray(props.order?.order_line_items)
        ? props.order.order_line_items.length
        : 0;
});

const normalizedLineItems = computed(() => {
    return Array.isArray(props.order?.order_line_items)
        ? props.order.order_line_items.map(normalizeItem)
        : [];
});

const filteredPendingItems = computed(() => {
    const query = String(itemSearchTerm.value || "").trim().toLowerCase();
    if (!query) return normalizedLineItems.value;

    return normalizedLineItems.value.filter((item) => {
        const haystack = `${item.name} ${item.variant}`.toLowerCase();
        return haystack.includes(query);
    });
});

const notShippedItems = computed(() => {
    const raw = props.order?.cancel_line_items;
    if (!Array.isArray(raw) || !raw.length) return [];
    return raw.map((item) => ({
        name: item.productName || item.product_name || item.name || "Item",
        variant: item.optionLabel || item.variant_name || item.variant || "",
        qty: toNumber(item.qty || item.quantity || 0),
        removed: toNumber(item.singlePrice || item.unit_price || 0) * toNumber(item.qty || item.quantity || 0),
        image: item.image_url || item.product_image || "/images/no-image.png",
    }));
});

const hasCancelItems = computed(() => notShippedItems.value.length > 0);

const notShippedUnits = computed(() => {
    return notShippedItems.value.reduce((sum, item) => sum + toNumber(item.qty), 0);
});

const notShippedProducts = computed(() => {
    return notShippedItems.value.length;
});

const notShippedTotal = computed(() => {
    return notShippedItems.value.reduce((sum, item) => sum + toNumber(item.removed), 0);
});

const totalUnits = computed(() => {
    const explicit = toNumber(props.order?.OrderLineItemsQuantity);
    if (explicit) return explicit;
    return normalizedLineItems.value.reduce((sum, item) => sum + toNumber(item.qty), 0);
});

const itemGroupsByShipment = computed(() => shipmentsSorted.value);

const sortItems = (items) =>
    [...items].sort((a, b) => {
        const ba = (a.brand || "").toLowerCase();
        const bb = (b.brand || "").toLowerCase();
        if (ba !== bb) return ba.localeCompare(bb);
        const na = (a.name || "").toLowerCase();
        const nb = (b.name || "").toLowerCase();
        if (na !== nb) return na.localeCompare(nb);
        return (a.variant || "").toLowerCase().localeCompare((b.variant || "").toLowerCase());
    });

const filteredItemGroupsByShipment = computed(() => {
    const query = String(itemSearchTerm.value || "").trim().toLowerCase();
    const groups = itemGroupsByShipment.value.map((shipment) => ({
        ...shipment,
        items: sortItems(Array.isArray(shipment.items) ? shipment.items : []),
    }));

    if (!query) return groups;

    return groups
        .map((shipment) => ({
            ...shipment,
            items: shipment.items.filter((item) => {
                const haystack = `${item.brand} ${item.name} ${item.variant} ${item.sku} ${item.upc}`.toLowerCase();
                return haystack.includes(query);
            }),
        }))
        .filter((shipment) => shipment.items.length);
});

const statusBucket = (shipment) => {
    const status = `${shipment.statusClass} ${shipment.status}`.toLowerCase();
    if (status.includes("deliver")) return "delivered";
    if (status.includes("transit")) return "in transit";
    if (status.includes("ship")) return "shipped";
    return "pending";
};

const shipmentStatusSummary = computed(() => {
    const buckets = { delivered: 0, "in transit": 0, shipped: 0, pending: 0 };
    shipmentsSorted.value.forEach((shipment) => {
        buckets[statusBucket(shipment)] += 1;
    });

    const active = Object.entries(buckets)
        .filter(([, count]) => count > 0)
        .map(([label, count]) => `${count} ${label}`);

    return active.join(" · ");
});

const shippingName = computed(() => {
    const ship = props.order?.shipping_address || {};
    const raw = ship.nickname || ship.company_name || [ship.first_name, ship.last_name].filter(Boolean).join(" ") || RAIL_FALLBACKS.shippingName;
    return raw.replace(/\s*\([A-Z]{2}\)$/, "").trim();
});

const shippingAddress = computed(() => {
    const ship = props.order?.shipping_address || {};
    const stateAbbr = ship.state_code || ship.state || "";
    const cityState = [ship.city, stateAbbr].filter(Boolean).join(", ");
    const cityStateLine = [cityState, ship.zip_code].filter(Boolean).join(" ");
    const resolved = [ship.address1, ship.address2, cityStateLine]
        .filter(Boolean)
        .join("\n");

    return resolved || RAIL_FALLBACKS.shippingAddress;
});

const invoiceNumber = computed(() => {
    const invoices = props.order?.invoice;
    if (!Array.isArray(invoices) || !invoices.length) return "";
    return invoices[0].invoice_number || "";
});

const invoiceLink = computed(() => {
    const invoices = props.order?.invoice;
    if (!Array.isArray(invoices) || !invoices.length) return "";
    return `/invoice/${invoices[0].id}`;
});

const placedBy = computed(() => {
    return (
        [props.order?.user?.name, props.order?.user?.last_name].filter(Boolean).join(" ") ||
        RAIL_FALLBACKS.placedBy
    );
});

const placedAt = computed(() => {
    return formatDate(props.order?.created_at) || RAIL_FALLBACKS.placedAt;
});

const displayPaymentStatus = computed(() => {
    return props.order?.payment_status || RAIL_FALLBACKS.paymentStatus;
});

const paymentStatusClass = computed(() => {
    const status = String(displayPaymentStatus.value || "").toLowerCase();
    if (status.includes("paid") && !status.includes("unpaid")) return "paid";
    if (status.includes("partial")) return "partial";
    if (status.includes("overdue") || status.includes("past due")) return "overdue";
    if (status.includes("unpaid") || status.includes("due") || status.includes("pending")) return "due";
    return "neutral";
});

const displayOrderNumber = computed(() => {
    return props.order?.order_number || RAIL_FALLBACKS.orderNumber;
});

const displayInvoiceNumber = computed(() => {
    return invoiceNumber.value || RAIL_FALLBACKS.invoiceNumber;
});

const displayReferenceNumber = computed(() => {
    return props.order?.reference_number || RAIL_FALLBACKS.referenceNumber;
});

const repName = computed(() => {
    return (
        [props.order?.sales_rep?.name, props.order?.sales_rep?.last_name].filter(Boolean).join(" ") ||
        [props.order?.representative?.name, props.order?.representative?.last_name].filter(Boolean).join(" ") ||
        RAIL_FALLBACKS.repName
    );
});

const repEmail = computed(() => {
    return props.order?.sales_rep?.email || props.order?.representative?.email || RAIL_FALLBACKS.repEmail;
});

const repPhone = computed(() => {
    return props.order?.sales_rep?.phone_number || props.order?.sales_rep?.phone || props.order?.representative?.phone_number || RAIL_FALLBACKS.repPhone;
});

const repInitials = computed(() => {
    const name = repName.value;
    if (!name) return "RP";
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
});

const orderInfoRows = computed(() => [
    { label: "Order #", value: displayOrderNumber.value, copyKey: "order-number", mono: true },
    { label: "Invoice #", value: displayInvoiceNumber.value, copyKey: "invoice-number", mono: true },
    { label: "Reference", value: displayReferenceNumber.value, copyKey: "reference-number", mono: true },
    { label: "Placed by", value: placedBy.value },
]);

const handleOrderInfoCopy = (copyKey, value) => {
    copyField(copyKey, value);
};

const summary = computed(() => {
    const subtotal = toNumber(props.order?.sub_total || props.order?.subTotal);
    const shipping = toNumber(props.order?.shippingTotal || props.order?.shipping_total);
    const tax = toNumber(props.order?.taxes || props.order?.tax_total);
    const total = toNumber(props.order?.orderTotal || props.order?.order_total || subtotal + shipping + tax);
    return { subtotal, shipping, tax, total };
});

const paymentTerm = computed(() => props.order?.payment_term || props.order?.paymentTerm || "");

const customerTerms = computed(() => {
    const term = props.order?.company?.payment_term?.name || "";
    return term === "Pre-Paid" ? null : term;
});

const isCreditCardTerm = computed(() => /credit\s*card|card/i.test(String(paymentTerm.value || "")));

const hasPaidStatus = computed(() => {
    const status = String(props.order?.payment_status || "").toLowerCase();
    return status.includes("paid") && !status.includes("unpaid");
});

const fallbackCardPayment = computed(() => {
    // UI fallback: keep the payment summary visually complete when paid credit-card metadata is missing.
    return {
        label:
            props.order?.payment_method ||
            props.order?.card_brand ||
            props.order?.card_type ||
            "Visa",
        isVisa: /visa/i.test(
            String(props.order?.payment_method || props.order?.card_brand || props.order?.card_type || "visa")
        ),
        last4:
            props.order?.last4 ||
            props.order?.last_4 ||
            props.order?.card_last4 ||
            props.order?.card_last_four ||
            "4242",
        reference: props.order?.transaction_id || props.order?.reference_number || "UI-DEMO",
        date: formatDate(
            props.order?.payment_date ||
            props.order?.paid_at ||
            props.order?.updated_at ||
            props.order?.created_at ||
            props.order?.date
        ),
        amount: toNumber(props.order?.total_paid || props.order?.amount_paid || summary.value.total),
    };
});

const detectCardBrand = (ccNumber) => {
    const first = String(ccNumber || "").replace(/\D/g, "")[0];
    if (first === "4") return "Visa";
    if (first === "5") return "Mastercard";
    if (first === "3") return "Amex";
    if (first === "6") return "Discover";
    return "Card";
};

const extractLast4 = (ccNumber) => {
    const digits = String(ccNumber || "").replace(/\*/g, "").trim();
    return digits.slice(-4) || "";
};

const paymentRows = computed(() => {
    const payments = Array.isArray(props.order?.payments) ? props.order.payments : [];
    const mappedPayments = payments.map((payment) => {
        const isCardPayment = payment.card != null || payment.card_id != null;
        const ccNumber = payment.card?.cc_number || "";
        const cardBrand = isCardPayment ? detectCardBrand(ccNumber) : "";
        const label = isCardPayment
            ? cardBrand
            : (props.order?.payment_term || props.order?.payment_method || "Bank Transfer");
        return {
            label,
            isVisa: cardBrand === "Visa",
            last4: isCardPayment ? extractLast4(ccNumber) : "",
            reference: payment.reference || payment.transaction_id || payment.auth_code || "",
            date: formatDate(payment.updated_at || payment.created_at || payment.date),
            amount: toNumber(payment.amount || payment.payable_amount || 0),
        };
    });

    if (mappedPayments.length) return mappedPayments;

    if (!isCreditCardTerm.value || !hasPaidStatus.value) {
        return mappedPayments;
    }

    return [fallbackCardPayment.value];
});

const summaryTotalLabel = computed(() => {
    return balance.value > 0 ? "Total Due" : "Total";
});

const summaryTotalAmount = computed(() => {
    return balance.value > 0 ? balance.value : summary.value.total;
});

const showPaidAmountRow = computed(() => {
    return balance.value > 0 && paidAmount.value > 0;
});

const paidAmount = computed(() => {
    const explicit = toNumber(props.order?.total_paid);
    if (explicit) return explicit;

    const fromPayments = paymentRows.value.reduce((sum, payment) => sum + toNumber(payment.amount), 0);
    if (fromPayments) return fromPayments;

    const paidStatus = String(props.order?.payment_status || "").toLowerCase();
    if (paidStatus.includes("paid") && !paidStatus.includes("unpaid")) {
        return summary.value.total;
    }
    return 0;
});

const balance = computed(() => {
    return Math.max(summary.value.total - paidAmount.value, 0);
});

const balanceClass = computed(() => {
    if (balance.value === 0) return "paid";
    const isOverdue = /overdue|past due/i.test(String(props.order?.payment_status || ""));
    return isOverdue ? "overdue" : "due";
});

const dueDate = computed(() => {
    return (
        formatDate(props.order?.payment_due_date || props.order?.due_date || props.order?.invoice_due_at) ||
        ""
    );
});

const overdueDays = computed(() => {
    const explicit = toNumber(props.order?.overdue_days || props.order?.overdueDays);
    if (explicit > 0) return explicit;
    if (balance.value <= 0) return 0;

    const due = parseDateValue(props.order?.payment_due_date || props.order?.due_date || props.order?.invoice_due_at);
    if (!due) return 0;

    const today = new Date();
    const dueStart = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diff = Math.floor((todayStart.getTime() - dueStart.getTime()) / 86400000);
    return diff > 0 ? diff : 0;
});

const carrierLogo = (carrier = "", shipment = null) => {
    if (/ltl/i.test(carrier) && shipment?.carrierImageUrl) {
        return shipment.carrierImageUrl;
    }
    if (/fedex/i.test(carrier)) return "/images/fedex.png";
    if (/ups/i.test(carrier)) return "/images/ups.png";
    if (/usps/i.test(carrier)) return "/images/usps.png";
    if (/dhl/i.test(carrier)) return "/images/DHL.png";
    if (/medusa/i.test(carrier)) return "/images/medusa.png";
    if (/better trucks/i.test(carrier)) return "/images/better-trucks.png";
    if(/ltl/i.test(carrier)) return "/images/odfl.jpeg";
    return "/images/no-image.png";
};

const carrierName = (carrier = "", shipment = null) => {
    if (/ltl/i.test(carrier) && shipment?.carrierName) {
        return shipment.carrierName;
    }
    if(/ltl/i.test(carrier)) return "Old Dominion Freight Line";
    return carrier;
};

const copyText = async (text) => {
    if (!text) return;

    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const input = document.createElement("textarea");
    input.value = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
};

const copySku = async (sku) => {
    if (!sku) return;
    await copyText(sku);
    copiedSku.value = sku;
    setTimeout(() => { if (copiedSku.value === sku) copiedSku.value = ""; }, 1200);
};

const copyTracking = async (shipment) => {
    if (!shipment.trackingNumber) return;
    await copyText(shipment.trackingNumber);
    copiedShipmentId.value = shipment.shipmentId;
    setTimeout(() => {
        if (copiedShipmentId.value === shipment.shipmentId) {
            copiedShipmentId.value = null;
        }
    }, 1200);
};

const copyAllTracking = async () => {
    const lines = shipmentsSorted.value
        .filter((shipment) => shipment.trackingNumber)
        .map((shipment) => `${shipment.carrier}: ${shipment.trackingNumber}`);

    if (!lines.length) return;

    await copyText(lines.join("\n"));
    copiedAllTracking.value = true;
    setTimeout(() => {
        copiedAllTracking.value = false;
    }, 1200);
};

const copyField = async (key, value) => {
    if (!value) return;
    await copyText(value);
    copiedFieldKey.value = key;
    setTimeout(() => {
        if (copiedFieldKey.value === key) {
            copiedFieldKey.value = "";
        }
    }, 1200);
};

const onRepContact = (e) => {
    e.preventDefault();
    document.querySelector("#rep-panel button")?.click();
};
</script>

<style scoped>
.of-layout {
    --of-line: rgba(230, 232, 238, 0.7);
    --of-line-soft: rgba(230, 232, 238, 0.55);
    --of-line-control: rgba(226, 229, 236, 0.78);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 18px;
    align-items: flex-start;
}

.of-main,
.of-rail {
    min-width: 0;
}

.of-rail {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.of-card {
    background: #fff;
    border: 1px solid var(--of-line);
    border-radius: 14px;
    overflow: hidden;
}

.of-panel-shipment {
    border: 0 !important;
    /* background: #f9f9f9; */
}

.of-card--flush {
    padding: 0;
    margin-top: 14px;
}

.of-pad {
    padding: 16px 18px;
}

.of-tabs-wrap {
    position: sticky;
    top: 60px;
    z-index: 10;
    background: #fff;
    border: 1px solid var(--of-line);
    border-radius: 12px;
    padding-right: 8px;
}

.of-tabs {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    border-bottom: none;
    padding: 0 2px 0 4px;
}

.of-tab {
    appearance: none;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 12px 16px 13px;
    margin-bottom: 0;
    font-size: 14px;
    font-weight: 600;
    color: #596275;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.of-tab.active {
    border-bottom-color: #bd966f;
    color: #2b303f;
}

.of-tab.warn.active {
    border-bottom-color: #b45309;
    color: #b45309;
}

.of-tab.disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.of-tab-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #b45309;
    flex-shrink: 0;
}

.of-pill {
    background: #f4f5f8;
    color: #596275;
    font-size: 11px;
    padding: 2px 7px;
    border-radius: 999px;
    font-weight: 600;
}

.of-pill.active {
    background: #f6eee6;
    color: #bd966f;
}

.of-pill.warn {
    background: #fef0d4;
    color: #b45309;
}

.of-tabs-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 8px;
    font-size: 12px;
    color: #78849a;
}

.of-panel {
    padding: 0px 0;
}

.of-items-header {
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    border-bottom: none;
    border-radius: 12px 12px 0 0;
}

.of-items-header-main {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.of-items-header-title {
    font-size: 13px;
    font-weight: 600;
    color: #3f4758;
}

.of-items-header-meta {
    font-size: 12px;
    color: #7a8395;
}

.of-items-search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    min-height: 36px;
    background: #f7f8fb;
    border: 1px solid var(--of-line-control);
    border-radius: 9px;
    min-width: 240px;
    max-width: 320px;
    flex: 1 1 0;
    color: #9aa3b4;
    transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
}

.of-items-search:focus-within {
    border-color: #cfd6e4;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(189, 150, 111, 0.14);
}

.of-items-search input {
    flex: 1 1 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #232a38;
    min-width: 0;
    padding: 0;
    @apply outline-none focus:ring-0 focus:ring-transparent;
}

.of-items-search input::placeholder {
    color: #a0a9ba;
}

.of-items-allocation {
    padding: 12px 20px;
    background: #fafbfd;
    border-bottom: 1px solid var(--of-line-soft);
    display: flex;
    align-items: center;
    gap: 10px;
}

.of-items-allocation-left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.of-items-allocation-title {
    font-size: 12px;
    font-weight: 600;
    color: #5c6578;
}

.of-items-alloc-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f2f4f8;
    color: #5f687b;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 999px;
}

.of-items-alloc-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #8a93a5;
    animation: ofAllocPulse 1.6s ease-in-out infinite;
}

.of-items-allocation-meta {
    margin-left: auto;
    font-size: 12px;
    color: #7a8395;
}

.of-items-table {
    display: grid;
    grid-template-columns: 1fr 80px 100px;
    padding: 8px 20px 14px;
    gap: 0;
}

.of-contained-table {
    margin: 12px 18px 0;
    padding: 12px 14px 14px;
    background: #fff;
    border: 1px solid #dfe4ec;
    border-radius: 10px;
}

.of-items-colhead {
    padding-bottom: 8px;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8c95a6;
    font-weight: 600;
}

.of-items-colhead.right {
    text-align: right;
}

.of-items-product-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
}

.of-items-qty-cell,
.of-items-total-cell {
    align-self: center;
    text-align: right;
    font-size: 13px;
}

.of-items-total-cell {
    font-weight: 400;
    color: #232a38;
}

.of-items-empty {
    grid-column: 1 / -1;
    padding: 10px 0 4px;
    font-size: 13px;
    color: #7a8395;
}

.of-package-items {
    background: #fff;
    border: 1px solid #e1e5ec;
    border-radius: 12px;
    overflow: hidden;
}

.of-package-items-head {
    min-height: 58px;
    padding: 12px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid #e6e9ef;
}

.of-package-title {
    font-size: 13px;
    font-weight: 600;
    color: #252d3b;
}

.of-package-total {
    margin-left: 4px;
    font-size: 12px;
    color: #8a94a8;
}

.of-package-group {
    border-bottom: 1px solid #e6e9ef;
}

.of-package-group:last-child {
    border-bottom: none;
}

.of-package-group-head {
    min-height: 44px;
    padding: 10px 18px;
    display: grid;
    grid-template-columns: 28px 58px minmax(140px, auto) auto auto 1fr;
    align-items: center;
    gap: 10px;
    background: #f8f9fc;
}

.of-package-number {
    font-size: 12px;
    font-weight: 600;
    color: #3f4758;
}

.of-package-logo {
    max-width: 52px;
    max-height: 18px;
    object-fit: contain;
    justify-self: start;
}

.of-package-carrier {
    font-size: 13px;
    font-weight: 600;
    color: #202838;
    white-space: nowrap;
}

.of-package-tracking {
    color: #4e586c;
    font-size: 12px;
    text-decoration-line: underline;
    text-decoration-style: dashed;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
    white-space: nowrap;
}

.of-package-group-head .of-status-pill {
    justify-self: end;
    grid-column: 6;
}

.of-package-table {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 72px 110px;
    column-gap: 16px;
    padding: 12px 18px 16px;
}

.of-package-colhead {
    padding-bottom: 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #8a94a8;
}

.of-package-colhead.right {
    text-align: right;
}

.of-package-product {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    min-width: 0;
}

.of-package-item-image {
    width: 34px;
    height: 34px;
    border-radius: 7px;
    object-fit: cover;
    background: #f1f3f7;
    flex-shrink: 0;
}

.of-package-item-main {
    min-width: 0;
}

.of-package-item-main p {
    margin: 0;
    font-size: 13px;
    line-height: 1.25;
    color: #202838;
    font-weight: 500;
}

.of-package-item-main span:not(.of-item-brand):not(.of-item-name):not(.of-item-sep) {
    display: block;
    margin-top: 2px;
    color: #6f7a8d;
    font-size: 12px;
}

.of-item-line1,
.of-item-line2 {
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    line-height: 1.3;
    flex-wrap: wrap;
}

.of-item-line1 {
    margin-bottom: 2px;
}

.of-item-brand {
    font-size: 12px;
    font-weight: 600;
    color: #1f2633;
}

.of-item-name {
    font-size: 12px;
    font-weight: 500;
    color: #1f2633;
}

.of-item-variant {
    font-size: 12px;
    color: #6f7a8d;
}

.of-item-sep {
    font-size: 11px;
    color: #c4cad4;
    flex-shrink: 0;
}

.of-item-meta-val {
    font-size: 12px;
    color: #6f7a8d;
    font-variant-numeric: tabular-nums;
}

.of-sku-copy-btn {
    display: inline-flex;
    align-items: center;
    color: #b0b8c6;
    padding: 1px 2px;
    border-radius: 3px;
    transition: color 0.12s;

    &:hover {
        color: #6f7a8d;
    }
}

.of-package-qty,
.of-package-total-cell {
    align-self: center;
    text-align: right;
    font-size: 13px;
    color: #202838;
    font-weight: 400;
    font-variant-numeric: tabular-nums;
}

.of-package-total-cell {
    font-weight: 500;
}

.of-not-shipped-card {
    background: #fff;
    border: 1px solid #dde2eb;
    border-radius: 14px;
    padding-bottom: 0;
    overflow: hidden;
}

.of-not-shipped-banner {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 16px 20px 18px;
    background: #fff8ec;
    border-bottom: 1px solid #e6c987;
    margin-bottom: 0;
}

.of-not-shipped-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #ffefd3;
    color: #b45309;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
}

.of-not-shipped-copy {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    line-height: 1.45;
    color: #111827;
}

.of-not-shipped-copy b {
    color: #0f172a;
    font-weight: 700;
}

.of-not-shipped-note {
    margin-top: 8px;
    color: #5c6678;
    font-size: 13px;
    line-height: 1.5;
}

.of-not-shipped-invoice {
    flex-shrink: 0;
    min-height: 34px;
    padding: 0 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    align-self: center;
}

.of-not-removed-cell {
    color: #1f2633;
    font-weight: 600;
}

.of-not-items-table {
    grid-template-columns: minmax(0, 1fr) 90px 130px;
    margin: 0;
    padding: 0;
}

.of-not-items-table .of-items-colhead {
    padding: 12px 20px;
    border-bottom: 1px solid #e1e5ec;
    font-size: 11px;
    letter-spacing: 0.1em;
}

.of-not-items-table .of-items-product-cell,
.of-not-items-table .of-items-qty-cell,
.of-not-items-table .of-items-total-cell {
    min-height: 54px;
    padding: 10px 20px;
    border-bottom: 1px solid #e1e5ec;
}

.of-not-items-table .of-items-product-cell {
    gap: 12px;
}

.of-not-items-table .of-item-image {
    width: 34px;
    height: 34px;
    border-radius: 7px;
}

.of-not-items-table .of-item-main p {
    font-size: 13px;
    font-weight: 500;
}

.of-not-items-table .of-item-main span {
    font-size: 12px;
    color: #5f6b7d;
}

.of-not-items-table .of-items-qty-cell,
.of-not-items-table .of-items-total-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 13px;
    color: #1f2633;
    font-variant-numeric: tabular-nums;
}

.of-not-adjust-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 90px 130px;
    align-items: center;
    margin: 0;
    padding: 14px 20px;
    background: #fafbfe;
    border: none;
    border-radius: 0;
}

.of-not-adjust-label {
    padding: 0;
    font-size: 13px;
    font-weight: 600;
    color: #111827;
}

.of-not-adjust-units {
    padding: 0;
    text-align: right;
    font-size: 13px;
    color: #8a94a8;
}

.of-not-adjust-total {
    padding: 0;
    text-align: right;
    font-size: 15px;
    font-weight: 600;
    color: #b45309;
}

.of-shipments-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    /* padding: 0 10px 2px; */
}

.of-shipment {
    background: #fff;
    border: none;
    border-radius: 14px;
    box-shadow: 0 0 0 1px #d3d7e074, 0 6px 18px -8px rgba(15, 23, 42, 0.05);
    overflow: hidden;
}

.of-shipment-head {
    width: 100%;
    padding: 16px 20px;
    cursor: pointer;
}

.of-head-grid {
    display: grid;
    grid-template-columns: 180px 1fr 1fr 130px 90px;
    gap: 14px;
    align-items: center;
}

.of-head-label {
    margin-bottom: 4px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #8c95a6;
    font-weight: 600;
}

.of-head-value {
    font-size: 13px;
    color: #3f4758;
    line-height: 1.35;
}

.of-head-value--strong {
    font-weight: 600;
    color: #1f2633;
}

.of-head-units {
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    color: #596275;
    font-size: 13px;
}

.of-head-units b {
    color: #222936;
    font-weight: 600;
}

.of-inline-row {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.of-inline-row a.of-head-value {
    color: #2f3545;
    text-decoration-line: underline;
    text-decoration-style: dashed;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
}

.of-inline-row a.of-head-value:hover {
    color: #bd966f;
}

.of-copy-btn,
.of-ghost-btn {
    appearance: none;
    border: 1px solid var(--of-line-control);
    background: #fff;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #3f4758;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 600;
}

.of-track-link-btn {
    padding: 3px 7px;
    min-height: 24px;
    justify-content: flex-start;
    gap: 4px;
    font-size: 10px;
    font-weight: 400;
    color: #3f4758;
    text-transform: none;
    letter-spacing: 0;
}

.of-link-btn {
    appearance: none;
    border: none;
    background: transparent;
    color: #2b303f;
    padding: 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
}

.of-link-btn:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
}

.of-copy-btn {
    border: none;
    padding: 0;
    width: 18px;
    height: 18px;
    justify-content: center;
    color: #7a8395;
}

.of-status-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
    text-transform: capitalize;
    background: #eef2f9;
    color: #4d5a73;
    line-height: 1.25;
}

.of-status-pill.delivered {
    background: #e5f4eb;
    color: #178244;
}

.of-status-pill.shipped,
.of-status-pill.in_transit,
.of-status-pill.transit {
    background: #f0f3fb;
    color: #526080;
}

.of-status-pill.open,
.of-status-pill.processing {
    background: #fbf2e6;
    color: #9a6a31;
}

.of-status-pill.cancelled,
.of-status-pill.void {
    background: #fdecec;
    color: #c45454;
}

.of-shipment-body {
    border-top: 1px solid var(--of-line-soft);
    background: #fafbfd;
    padding: 20px 24px;
}

.of-shipment-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 !important;
}

.of-shipment-grid.pickup {
    grid-template-columns: 1fr;
}

.of-block-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #8c95a6;
    font-weight: 600;
}

.of-block-head--inline {
    justify-content: flex-start;
    gap: 10px;
}

.of-timeline-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.of-timeline-item {
    position: relative;
    display: flex;
    gap: 12px;
    padding-bottom: 20px;
}

.of-timeline-item:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 17px;
    bottom: 2px;
    width: 1px;
    background: #efe9e2;
}

.of-timeline-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #c8a985;
    margin-top: 4px;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
    box-shadow: 0 0 0 3px #fbf6f1;
}

.of-timeline-dot.pending {
    background: #fff;
    border: 2px solid #d2b896;
}

.of-timeline-item p {
    font-size: 13px;
    color: #2b303f;
    font-weight: 500;
    margin: 0;
}

.of-timeline-item span {
    font-size: 12px;
    color: #7a8395;
}

.of-box-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.of-box {
    border: 1px solid #dfe4ec;
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 1px 1px rgba(15, 23, 42, 0.02);
}

.of-box.expanded {
    border-color: #d8dde8;
}

.of-box-head {
    min-height: 44px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    background: #fff;
    transition: background-color 140ms ease, border-color 140ms ease;
}

.of-box-head.expanded {
    background: #eef0f5;
}

.of-box-label {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #1f2633;
    font-weight: 600;
}

.of-box-label span:last-child {
    color: #8a94a8;
    font-size: 10px;
    font-weight: 500;
}

.of-box-meta {
    font-size: 11px;
    color: #7d8798;
    font-weight: 400;
}

.of-box-items {
    display: flex;
    flex-direction: column;
}

.of-items-in-shipment {
    background: #fff;
}

.of-items-in-shipment .of-item-row:first-child {
    border-top: none;
}

.of-item-row {
    display: grid;
    grid-template-columns: 34px 38px minmax(0, 1fr);
    gap: 9px;
    align-items: center;
    padding: 10px 12px;
    border-top: 1px solid var(--of-line-soft);
}

.of-item-image {
    grid-column: 2;
    width: 34px;
    height: 34px;
    border-radius: 7px;
    object-fit: cover;
    background: #f3f5f8;
}

.of-item-main {
    grid-column: 3;
    min-width: 0;
}

.of-item-main p {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: #1f2633;
    line-height: 1.25;
}

.of-item-main span {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: #727d90;
}

.of-item-qty {
    grid-column: 1;
    grid-row: 1;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 3px;
    min-width: 34px;
    font-size: 13px;
    font-weight: 600;
    color: #1f2633;
    font-variant-numeric: tabular-nums;
}

.of-item-qty-mark {
    color: #8a94a8;
    font-size: 13px;
    font-weight: 500;
}

.of-show-more-btn {
    appearance: none;
    border: none;
    background: transparent;
    color: #8f6f4f;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    padding: 0;
    font-size: 12px;
    font-weight: 500;
}

.of-show-more-btn:hover {
    color: #bd966f;
}

.of-items-head {
    padding: 12px 18px;
    background: #fafbfd;
    border-top: 1px solid var(--of-line-soft);
    border-bottom: 1px solid var(--of-line-soft);
    display: flex;
    justify-content: space-between;
    gap: 12px;
    color: #606a7d;
    font-size: 12px;
    font-weight: 600;
}

@keyframes ofAllocPulse {

    0%,
    100% {
        opacity: 0.35;
    }

    50% {
        opacity: 1;
    }
}

.of-items-group {
    border-top: 1px solid var(--of-line-soft);
}

.of-items-group-head {
    width: 100%;
    appearance: none;
    border: none;
    background: #fafbfd;
    padding: 12px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #3f4758;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.of-empty-state {
    padding: 24px 20px;
}

.of-empty-state h4 {
    margin: 0;
    font-size: 15px;
    color: #2b303f;
}

.of-empty-state p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7a8395;
}

.of-rail-label {
    margin-bottom: 8px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #8c95a6;
    font-weight: 600;
}

.of-info-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
}

.of-info-topline .of-rail-label {
    margin-bottom: 0;
}

.of-payment-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
}

.of-payment-badge.paid {
    background: #e9f6ed;
    color: #1f8a49;
}

.of-payment-badge.partial {
    background: #fff5e8;
    color: #b96b18;
}

.of-payment-badge.due,
.of-payment-badge.overdue {
    background: #fdf0f1;
    color: #c76b7b;
}

.of-payment-badge.neutral {
    background: #eef2f6;
    color: #596275;
}

.of-rail-title {
    color: #222936;
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 4px;
    line-height: 1.35;
}

.of-rail-text {
    white-space: pre-line;
    color: #586173;
    font-size: 13px;
    line-height: 1.6;
}

.of-info-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.of-info-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    font-size: 13px;
}

.of-info-value-copy {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    min-width: 0;
    text-align: right;
}

.of-info-row>span:first-child {
    color: #6d7688;
}

.of-info-row>span:last-child {
    color: #242c3a;
    font-weight: 600;
    text-align: right;
}

.of-info-row--paid>span:first-child,
.of-info-row--paid>span:last-child {
    color: #1f8a49;
    font-weight: 600;
}

.of-copy-btn--inline {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: #8a93a5;
}

.of-rep {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--of-line-soft);
}

.of-rep-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.of-rep-avatar {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: #ecdebf;
    color: #7a5b14;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
}

.of-rep-name {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #232a38;
}

.of-rep-link {
    display: block;
    font-size: 12px;
    color: #4b5568;
    text-decoration: underline;
    margin-top: 2px;
}

.of-rep-link--action {
    color: #2b303f;
    font-weight: 600;
}

.of-summary-head {
    padding: 16px 20px 14px;
    border-bottom: 1px solid var(--of-line-soft);
    font-size: 15px;
    font-weight: 600;
    color: #232a38;
}

.of-summary-rows {
    padding: 16px 20px 4px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.of-summary-total {
    padding: 14px 20px;
    border-top: 2px solid var(--of-line);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 15px;
    font-weight: 700;
    color: #1f2633;
}

.of-summary-total>span:last-child {
    font-size: 21px;
}

.of-summary-payments {
    padding: 14px 20px 16px;
    border-top: 1px solid var(--of-line-soft);
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.of-payment-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    font-size: 13px;
    color: #5b6476;
    gap: 8px;
}

.of-payment-row-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    min-width: 0;
}

.of-payment-row-main {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
}

.paid-mark {
    color: #1f8a49;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.of-payment-paid-label {
    color: #5b6476;
}

.of-payment-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    padding: 0 6px;
    background: #1a1f71;
    color: #fff;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    line-height: 1;
}

.of-payment-method {
    color: #3f4758;
    font-weight: 600;
}

.of-payment-last4,
.of-payment-reference {
    color: #6d7688;
    font-size: 12px;
}

.of-payment-date {
    color: #8a93a5;
    font-size: 11px;
    padding-left: 18px;
}

.of-payment-amount {
    font-size: 13px;
    color: #232a38;
    font-weight: 500;
    flex-shrink: 0;
}

.of-payment-summary {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.35;
}

.of-payment-summary.paid {
    color: #1f8a49;
}

.of-payment-summary.due {
    color: #b45309;
}

.of-payment-summary.overdue {
    color: #c15454;
}

.of-pay-btn {
    appearance: none;
    border: 1px solid rgba(43, 48, 63, 0.86);
    background: #2b303f;
    color: #fff;
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: opacity 160ms ease, background-color 160ms ease;
}

.of-pay-btn:hover {
    background: #2b303f;
    opacity: 0.95;
}

.of-summary-footer {
    border-top: 1px solid var(--of-line-soft);
    padding: 14px 20px;
    background: #fafbfd;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.of-due-date-value.overdue {
    color: #c15454;
    font-weight: 700;
}

.of-overdue-note {
    font-size: 12px;
    font-weight: 600;
    color: #c15454;
    text-align: right;
}

.of-ghost-btn.of-full {
    justify-content: center;
    margin-top: 4px;
}

.of-carrier-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.of-carrier-logo {
    width: 30px;
    height: 14px;
    object-fit: contain;
}

/* ── LTL / PICKUP HEAD VARIANTS ─────────────────────────── */
.of-head-grid--ltl,
.of-head-grid--pickup {
    grid-template-columns: 180px 1fr 1fr 130px 110px;
}

.of-head-freight-meta {
    margin-top: 3px;
    font-size: 11px;
    color: #8c95a6;
    font-weight: 500;
}

.of-head-pro-number {
    margin-top: 3px;
    font-size: 11px;
    color: #8c95a6;
}

.of-head-expected {
    margin-top: 2px;
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
    line-height: 1.3;
}

.of-head-expected.delivered {
    color: #4b5568;
    font-style: normal;
    font-weight: 500;
}

.of-head-expected.expected {
    color: #6b7280;
}

.of-head-units-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
}

.of-head-units-sub {
    font-size: 11px;
    color: #8c95a6;
}

.of-head-value--sub {
    font-size: 11px;
    color: #7a8395;
    margin-top: 1px;
}

/* ── PICKUP ICON ────────────────────────────────────────── */
.of-pickup-icon {
    width: 28px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
}

/* ── LAST SCAN / DELIVERED BANNER ───────────────────────── */
.of-last-scan-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: -2px 20px 12px;
    padding: 7px 10px;
    background: #f6f8fc;
    border: 1px solid #e0e5ef;
    border-radius: 8px;
    font-size: 11px;
}

.of-last-scan-bar.delivered {
    background: #f1f8f4;
    border-color: #d7eadf;
}

.of-last-scan-bar.confirmed {
    background: #f1f8f4;
    border-color: #d7eadf;
}

.of-last-scan-bar.transit {
    background: #f6f8fc;
    border-color: #e0e5ef;
}

.of-last-scan-pin {
    width: 20px;
    height: 20px;
    padding: 3px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid #d7ddeb;
    flex-shrink: 0;
    color: #64708a;
}

.of-last-scan-bar.delivered .of-last-scan-pin,
.of-last-scan-bar.confirmed .of-last-scan-pin {
    color: #2c7a49;
    border-color: #cae4d3;
}

.of-last-scan-label {
    font-size: 11px;
    font-weight: 500;
    color: #374151;
    flex-shrink: 0;
}

.of-last-scan-bar.delivered .of-last-scan-label,
.of-last-scan-bar.confirmed .of-last-scan-label {
    color: #374151;
}

.of-last-scan-event {
    color: #64708a;
    font-weight: 400;
    flex: 1;
    min-width: 0;
}

.of-last-scan-time {
    color: #64708a;
    font-size: 11px;
    font-weight: 400;
    flex-shrink: 0;
}

/* ── PICKUP CONFIRMED BANNER ────────────────────────────── */
.of-pickup-confirmed-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: #edf7f1;
    border-bottom: 1px solid #c3e6cc;
    font-size: 12px;
    color: #1f8a49;
}

.of-pickup-confirmed-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #1f8a49;
}

.of-pickup-confirmed-status {
    font-weight: 600;
    color: #2b303f;
}

.of-pickup-confirmed-time {
    margin-left: auto;
    color: #7a8395;
    font-size: 11px;
}

/* ── BOX HEAD WITH CARTON CHIP ──────────────────────────── */
.of-box-head--carton {
    display: grid;
    grid-template-columns: minmax(108px, auto) minmax(126px, 1fr) auto;
    align-items: center;
    gap: 12px;
}

.of-carton-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    font-size: 11px;
    color: #5c6578;
    font-weight: 400;
    min-width: 0;
}

.of-carton-sep {
    color: #b0b8c8;
}

.of-carton-weight {
    color: #1f2937;
    font-weight: 500;
}

.of-carton-dims {
    color: #8a94a8;
    font-weight: 500;
}

/* ── STANDARD CARTON BLOCK ──────────────────────────────── */
.of-carton-block {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background: #fff;
    border: 1px solid var(--of-line);
    border-radius: 8px;
    margin-bottom: 14px;
}

.of-carton-icon {
    width: 18px;
    height: 16px;
    flex-shrink: 0;
}

.of-carton-text {
    font-size: 12px;
    color: #4c5568;
    font-weight: 500;
}

@media (max-width: 1200px) {
    .of-head-grid {
        grid-template-columns: 1fr 1fr;
        row-gap: 10px;
    }

    .of-head-grid--ltl,
    .of-head-grid--pickup {
        grid-template-columns: 1fr 1fr;
        row-gap: 10px;
    }

    .of-head-units {
        justify-content: flex-start;
    }

    .of-head-units-stack {
        align-items: flex-start;
    }

    .of-shipment-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 1024px) {
    .of-layout {
        grid-template-columns: 1fr;
    }

    .of-tabs {
        flex-wrap: wrap;
    }

    .of-tabs-right {
        width: 100%;
        margin-left: 0;
        padding: 0 0 10px 8px;
    }

    .of-items-header {
        flex-direction: column;
        align-items: stretch;
    }

    .of-items-search {
        max-width: none;
        min-width: 0;
    }

    .of-items-allocation {
        flex-wrap: wrap;
    }

    .of-items-allocation-meta {
        margin-left: 0;
    }

    .of-not-shipped-banner {
        flex-wrap: wrap;
    }

    .of-not-shipped-invoice {
        margin-left: 40px;
    }

    .of-not-adjust-row {
        grid-template-columns: 1fr 80px 105px;
    }
}
</style>
